import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import PhotoUpload from "../PhotoUpload/PUpload";
import { RotatingLines } from "react-loader-spinner";
import toast from "react-hot-toast";
import { refreshTokenAction } from "../../Redux/actions/auth";

const Request = () => {
  const dispatch = useDispatch();
  const userFromStore = useSelector((state) => state.auth.user);
  const [user, setUser] = useState(
    userFromStore || JSON.parse(localStorage.getItem("user"))
  );
  const [serviceTypes, setServiceTypes] = useState([]);
  const fullName = user?.name;
  const phoneNumber = user?.phone_number;
  const postCode = user?.postal_code;
  const token = user?.token;
  const [imageFile, setImageFile] = useState(null);
  const [contactTime, setContactTime] = useState("");
  const [contactDate, setContactDate] = useState("");
  const [serviceTypeId, setServiceTypeId] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    let errors = {};

    if (!contactTime) {
      errors.contactTime = "Contact time is required";
    }
    if (!contactDate) {
      errors.contactDate = "Contact date is required";
    }
    if (!serviceTypeId) {
      errors.serviceTypeId = "Service type is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTokenRefresh = async (token) => {
    console.log("Attempting to refresh token with:", token);

    try {
      const response = await fetch(
        "https://backend.pmc-group.uk/api/refresh-token",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        console.error(
          "Token refresh failed with status:",
          response.status,
          response.statusText
        );
        throw new Error("Token refresh failed");
      }

      const data = await response.json();

      dispatch(refreshTokenAction(data.newToken)); // Dispatch action to update token
      console.log("Token refreshed successfully:", data.newToken);

      // Update local user state with the new token
      const updatedUser = { ...user, token: data.newToken };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return data.newToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      throw error;
    }
  };
  const fetchServiceTypes = async () => {
    // console.log("Fetching service types...");
    try {
      const response = await fetch(
        "https://backend.pmc-group.uk/api/service-type",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // console.log("Fetch response status:", response.status);
      if (!response.ok) {
        console.error(
          "Failed to fetch service types. Status:",
          response.status
        );
        throw new Error(
          `Failed to fetch service types: ${response.statusText}`
        );
      }

      const data = await response.json();
      // console.log("Service types fetched successfully:", data);
      setServiceTypes(data?.data || []);
    } catch (error) {
      console.error("Error fetching service types:", error);
      toast.error("Failed to load service types.");
    }
  };

  useEffect(() => {
    if (token) {
      fetchServiceTypes();
    }
  }, [token]);

  const resetForm = () => {
    setContactTime("");
    setContactDate("");
    setServiceTypeId("");
    setDescription("");
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const formData = new FormData();

    // Append all required fields to the FormData object
    formData.append("phone_number", user.phone_number);
    formData.append("contact_time", contactTime);
    formData.append("contact_date", contactDate);
    formData.append("post_code", user.postal_code);
    formData.append("service_type_id", serviceTypeId);
    formData.append("description", description);
    formData.append("full_name", user.name);

    //Trying to Append the image file if it exists omo
    if (imageFile) {
      console.log("Image File:", imageFile);
      console.log("Image File Name:", imageFile.name);
      formData.append("image", imageFile, imageFile.name);
    }

    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }

    let currentToken = token;

    setLoading(true);
    try {
      console.log("Using token:", currentToken);
      const response = await fetch("https://backend.pmc-group.uk/api/request", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${currentToken}`,
          // Do not set 'Content-Type' header when sending FormData
        },
        body: formData,
      });

      if (!response.ok) {
        console.error(
          "Initial request failed with status:",
          response.status,
          response.statusText
        );

        if (response.status === 400) {
          const errorData = await response.json();
          console.error("Server response for 400 error:", errorData);
        }

        if (response.status === 401) {
          // If unauthorized, try to refresh token
          console.log("Unauthorized, attempting to refresh token...");
          currentToken = await handleTokenRefresh(token);

          // Retry the request with the new token
          console.log("Retrying request with new token:", currentToken);
          const retryResponse = await fetch(
            "https://backend.pmc-group.uk/api/request",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
              body: formData,
            }
          );

          if (!retryResponse.ok) {
            console.error(
              `Request failed after token refresh with status: ${retryResponse.status}`,
              retryResponse.statusText
            );
            throw new Error(
              `Request failed after token refresh: ${retryResponse.status} ${retryResponse.statusText}`
            );
          }

          const retryData = await retryResponse.json();
          console.log("Request successful after token refresh:", retryData);
          toast.success("Request submitted successfully after token refresh.");
          resetForm();
        } else {
          throw new Error(
            `Request failed: ${response.status} ${response.statusText}`
          );
        }
      } else {
        const data = await response.json();
        console.log("Request successful:", data);
        toast.success("Request submitted successfully.");
        resetForm();
      }
    } catch (error) {
      console.error("Error during fetch:", error);
      toast.error("Request submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const todayDate = new Date().toISOString().split("T")[0]; // Get today's date to ensure user doesnt select past dates

  return (
    <div className="p-0 m-0 box-border font-Urbanist">
      <div className="w-[90%] mx-auto pt-1 lg:pt-4 pb-12">
        <div className="w-full mx-auto py-8 shadow-xl bg-white rounded-lg">
          <h2 className="text-xl text-navBg font-semibold px-6 py-4">
            Make a New Request
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="p-6 flex flex-col lg:flex-row justify-start lg:justify-between items-center gap-4">
              <div className="relative gap-2 w-full">
                <input
                  type="text"
                  value={user?.name}
                  className="border-1 w-full lg:w-auto peer block appearance-none rounded-lg border border-gray-300 bg-white px-2.5 pb-2.5 pt-4 text-sm text-black focus:border-navBg focus:outline-none focus:ring-0"
                  placeholder="Full Name"
                  readOnly
                />
                <label className="absolute bg-white top-2 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none px-2 text-sm font-semibold text-navBg duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-navBg">
                  Full Name
                </label>
              </div>
              <div className="relative gap-2 w-full">
                <input
                  type="tel"
                  value={user?.phone_number}
                  className="border-1 w-full lg:w-auto peer block appearance-none rounded-lg border border-gray-300 bg-white px-2.5 pb-2.5 pt-4 text-sm text-black focus:border-navBg focus:outline-none focus:ring-0"
                  placeholder="Phone Number"
                  readOnly
                />
                <label className="absolute bg-white top-2 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none px-2 text-sm font-semibold text-navBg duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-navBg">
                  Phone Number
                </label>
              </div>
              <div className="relative gap-2 mt-2 w-full">
                <input
                  type="time"
                  className="border-1 w-full lg:w-[150px] peer block appearance-none rounded-lg border border-gray-300 bg-white px-2.5 pb-2.5 pt-4 text-sm text-black focus:border-navBg focus:outline-none focus:ring-0"
                  placeholder="Contact Time"
                  value={contactTime}
                  onChange={(e) => setContactTime(e.target.value)}
                />
                {formErrors.contactTime && (
                  <span className="text-red-500">{formErrors.contactTime}</span>
                )}
                <label className="absolute bg-white text-navBg top-2 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none px-2 text-sm font-semibold duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-navBg">
                  Contact Time
                </label>
              </div>
              <div className="relative gap-2 mt-4 w-full">
                <input
                  type="date"
                  className="border-1 w-full lg:w-auto peer block appearance-none rounded-lg border border-gray-300 bg-white px-2.5 pb-2.5 pt-4 text-sm text-black focus:border-navBg focus:outline-none focus:ring-0"
                  placeholder="Contact Date"
                  value={contactDate}
                  min={todayDate} // Prevent selecting past dates
                  onChange={(e) => setContactDate(e.target.value)}
                />
                {formErrors.contactDate && (
                  <span className="text-red-500">{formErrors.contactDate}</span>
                )}
                <label className="absolute bg-white top-0 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none px-2 text-sm font-semibold text-navBg duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-navBg">
                  Contact Date
                </label>
              </div>
            </div>
            {/* 2nd form */}
            <div className="py-1 px-6 lg:p-6 flex flex-col lg:flex-row justify-start lg:justify-between items-center gap-2 lg:gap-4">
              <div className="relative gap-2 w-full">
                <input
                  type="text"
                  value={user?.postal_code}
                  className="border-1 w-full lg:w-[150px] peer block appearance-none rounded-lg border border-gray-300 bg-white px-2.5 pb-2.5 pt-4 text-sm text-black focus:border-navBg focus:outline-none focus:ring-0"
                  placeholder="Post Code"
                  readOnly
                />
                <label className="absolute bg-white top-2 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none px-2 text-sm font-semibold text-navBg duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-navBg">
                  Post Code
                </label>
              </div>
              <PhotoUpload setImageFile={setImageFile} />
              {/* <div className="p-6 flex flex-col lg:flex-row justify-start lg:justify-between items-center gap-4"> */}
              <div className="relative gap-2 w-full">
                <select
                  className="border-1 w-full peer block appearance-none rounded-lg border border-gray-300 bg-white px-2.5 pb-2.5 pt-4 text-sm text-black focus:border-navBg focus:outline-none focus:ring-0"
                  value={serviceTypeId}
                  onChange={(e) => setServiceTypeId(e.target.value)}
                >
                  <option value="">Select Service Type</option>
                  {serviceTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {formErrors.serviceTypeId && (
                  <span className="text-red-500">
                    {formErrors.serviceTypeId}
                  </span>
                )}
              </div>
              {/* </div> */}
              <div className="relative gap-2 w-full mt-2">
                <input
                  type="text"
                  className="border-1 w-full peer block appearance-none rounded-lg border border-gray-300 bg-white px-2.5 pb-2.5 pt-4 text-sm text-black focus:border-navBg focus:outline-none focus:ring-0"
                  placeholder="Describe Job Details"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                {formErrors.description && (
                  <span className="text-red-500">{formErrors.description}</span>
                )}
                <label className="absolute bg-white top-0 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none px-2 text-sm font-semibold text-navBg duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-navBg">
                  Describe Job Details
                </label>
              </div>
            </div>
            <div className="flex items-center justify-center mt-6">
              <button
                type="submit"
                className="py-2 px-6 bg-navBg text-white rounded-md shadow-lg hover:bg-gray-800 transition duration-300"
                disabled={loading}
              >
                {loading ? (
                  <RotatingLines
                    strokeColor="#fff"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="20"
                    visible={true}
                  />
                ) : (
                  "Submit Request"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Request;
